import { blinkConstants } from '@thoughtspot/blink-constants';
import { FISCAL_CALENDAR_NAME, getCustomCalendarGuid, getEndEpochMS, getStartEpochMS, } from '@thoughtspot/custom-calendar-service';
import { convertToMilliSeconds, formatDate, formatEpochMillisToFiscalYear, getNextBucketEpoch, getTimeBucketStart, subtractFromField, timeBucketCategories, timeBuckets, } from '@thoughtspot/date-format-util';
import { getFormattingFromColumn, } from '@thoughtspot/formatter-service';
import { CategoryType, Unit, } from '@thoughtspot/formatter-service/esm/types/prism';
import { dateFormats, translate, translateLazy } from '@thoughtspot/i18n';
import { arrayHasDuplicates } from '@thoughtspot/js-util';
import { formatDataValue, getValueFormattedByConfig, } from '@thoughtspot/number-format-util';
import { isCSSVariableApplied } from '@thoughtspot/radiant-react/widgets/color/color-utils';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { isChartAggregatedValOnKPIEnabled, isGenericCustomCompareEnabled, isKPIChartEnhancementsEnabled, } from '@thoughtspot/session-service';
import { getCustomCalendarGuidFromColumn, getTimeBucket, hasCustomCalendar, isMeasure, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { ErrorTypes } from '../../core/chart-data-validation';
import { areAllTimeSeriesColumns, getAxisConfig, getChartSpecificCustomProps, getColumnCustomProps, getColumnPropertiesById, getCustomColorSelectorArray, getHandleMissingValues, getXAxisColumns, getYAxisColumns, isCustomCalendarTimeSeries, isDescendingSort, } from '../../core/chart-model-utils';
import { chartSpecificConstants, containsAttributeColumn, incompleteZoneConfigTemplate1, } from '../../core/chart-type-specification-service';
import { isConditionalFormattingApplied } from '../../core/conditional-formatting-utils';
import { getSeries } from '../../data/data-util';
import { getEnvFlags, getTranslatedTextCallback } from '../../env';
import { addAnomalyExpectationBarAndLine } from '../../reactcharts/secondary-render-util';
import { ChangeInterpretation, ColumnTimeBucket, HandleMissingValues, ShowChangeAs, TupleType, } from '../../types';
import { createLogger } from '../../utils/logger';
import { getAnomalyColor, hasCortexData, isPointAnomaly, isShowAnomaliesEnabled, } from './cortex-utils';
const logger = createLogger('highchart-kpi-renderer');
export var ErrorCodes;
(function (ErrorCodes) {
    ErrorCodes["COMPARISON_DATE_OUT_OF_RANGE"] = "COMPARISON_DATE_OUT_OF_RANGE";
    ErrorCodes["MAX_OFFSET_LIMIT"] = "MAX_OFFSET_LIMIT";
})(ErrorCodes || (ErrorCodes = {}));
export var KPIChartType;
(function (KPIChartType) {
    KPIChartType["MEASURE"] = "single-value-numeric";
    KPIChartType["TIMESERIES"] = "time-series";
    KPIChartType["ATTRIBUTE"] = "single-value-non-numeric";
})(KPIChartType || (KPIChartType = {}));
export const validXAxisColumnTimeBuckets = [
    ColumnTimeBucket.Hourly,
    ColumnTimeBucket.Daily,
    ColumnTimeBucket.Weekly,
    ColumnTimeBucket.Monthly,
    ColumnTimeBucket.Quarterly,
    ColumnTimeBucket.Yearly,
];
export const getComparisionLabelFromTimeBucket = (timeBucket) => ({
    [timeBuckets.HOURLY]: getTranslatedTextCallback()('comparison.timeBucket.hour'),
    [timeBuckets.DAILY]: getTranslatedTextCallback()('comparison.timeBucket.day'),
    [timeBuckets.WEEKLY]: getTranslatedTextCallback()('comparison.timeBucket.week'),
    [timeBuckets.MONTHLY]: getTranslatedTextCallback()('comparison.timeBucket.month'),
    [timeBuckets.QUARTERLY]: getTranslatedTextCallback()('comparison.timeBucket.quarter'),
    [timeBuckets.YEARLY]: getTranslatedTextCallback()('comparison.timeBucket.year'),
}[timeBucket]);
export const getLabelFormat = (timeBucket) => ({
    [timeBuckets.HOURLY]: dateFormats.DATE_SHORT_WITH_HOUR,
    [timeBuckets.DAILY]: dateFormats.DATE_SHORT,
    [timeBuckets.WEEKLY]: dateFormats.DATE_SHORT,
    [timeBuckets.MONTHLY]: dateFormats.MONTH_WITH_YEAR,
    [timeBuckets.QUARTERLY]: dateFormats.QUARTER_WITH_YEAR,
    [timeBuckets.YEARLY]: 'yyyy',
}[timeBucket]);
const isNumberPositive = (diffValue) => diffValue >= 0;
export const isNumberInfinite = (diffValue) => diffValue === Number.POSITIVE_INFINITY ||
    diffValue === Number.NEGATIVE_INFINITY ||
    Number.isNaN(diffValue);
export const getChangeIcon = (diffValue) => {
    if (isNumberInfinite(diffValue)) {
        return null;
    }
    if (isNumberPositive(diffValue)) {
        return IconID.ARROW_UP;
    }
    return IconID.ARROW_DOWN;
};
export const getFormattedDateTime = (epochValue, timeColumn, shouldTranslateText = true, options) => {
    const timeBucket = !_.isEmpty(timeColumn) && getTimeBucket(timeColumn);
    const formattedValue = formatDate(epochValue, getLabelFormat(timeBucket), false, options);
    if (shouldTranslateText && timeBucket === timeBuckets.WEEKLY) {
        return getTranslatedTextCallback()('weekOfDate', {
            date: formattedValue,
        });
    }
    return formattedValue;
};
export const positiveComparisionColorIndex = 0;
export const negativeComparisionColorIndex = 1;
export const getKpiChartType = (chartModel) => {
    const axisConfig = getAxisConfig(chartModel);
    if (_.isEmpty(axisConfig.x)) {
        const isAttribute = containsAttributeColumn(getYAxisColumns(chartModel));
        if (isAttribute)
            return KPIChartType.ATTRIBUTE;
        return KPIChartType.MEASURE;
    }
    if (areAllTimeSeriesColumns(axisConfig.x) &&
        !_.some(axisConfig.x, column => isDescendingSort(chartModel, column))) {
        return KPIChartType.TIMESERIES;
    }
    return null;
};
export const isKpiChartTypeTimeseries = (chartModel) => {
    return getKpiChartType(chartModel) === KPIChartType.TIMESERIES;
};
export const shouldIncludeEnhancedKpiSettings = (isTimeSeries) => {
    return isKPIChartEnhancementsEnabled() && isTimeSeries;
};
export const isLabelEnabled = (chartModel) => {
    var _a, _b;
    return (_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showLabel) !== null && _b !== void 0 ? _b : isKpiChartTypeTimeseries(chartModel);
};
export const isComparisionEnabled = (chartModel) => {
    var _a, _b;
    return (_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showComparison) !== null && _b !== void 0 ? _b : (isKpiChartTypeTimeseries(chartModel) ||
        isSinglePointKPIEnabledWithValue(chartModel));
};
export const isAnalyseChangeCtaEnabled = (chartModel) => {
    var _a, _b;
    return (_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showAnalyseChangeCta) !== null && _b !== void 0 ? _b : (isKpiChartTypeTimeseries(chartModel) ||
        isSinglePointKPIEnabledWithValue(chartModel));
};
export const getPositiveComparisionColor = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), positiveComparisionColorIndex, chartSpecificConstants.DEFAULT_KPI_COLORS[positiveComparisionColorIndex]);
export const getNegativeComparisionColor = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), negativeComparisionColorIndex, chartSpecificConstants.DEFAULT_KPI_COLORS[negativeComparisionColorIndex]);
export const getPositiveComparisionColorForNewKPI = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), positiveComparisionColorIndex, chartSpecificConstants.DEFAULT_NEW_KPI_COLORS[positiveComparisionColorIndex]);
export const getNegativeComparisionColorForNewKPI = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), negativeComparisionColorIndex, chartSpecificConstants.DEFAULT_NEW_KPI_COLORS[negativeComparisionColorIndex]);
export const getKeyByColorCodeFromGradientColorsPalette = (colorCode) => {
    for (const [key, colors] of Object.entries(blinkConstants.gradientColorsColorPalette)) {
        if (colors.includes(colorCode)) {
            return key;
        }
    }
    return null;
};
export const getKeyByColorCodeFromDefaultColorsPalette = (colorCode) => {
    for (const [key, colors] of Object.entries(blinkConstants.defaultColorPalette)) {
        if (colors.includes(colorCode)) {
            return key;
        }
    }
    return null;
};
export const getColorCodeByKeyAndIndexFromGradientColorsPalette = (key, index = 3) => {
    const gradientColorsColorPalette = blinkConstants.gradientColorsColorPalette;
    if (gradientColorsColorPalette.hasOwnProperty(key)) {
        const colors = gradientColorsColorPalette[key];
        if (index >= 0 && index < colors.length) {
            return colors[index];
        }
        return null;
    }
    return null;
};
export const infiniteComparisionColor = '#1c2330';
export const infiniteDisplayDiffValue = '--';
export const zeroDiffValueBgColor = '#EAEDF2';
export const zeroDiffValueColor = '#777E8B';
export const getChangeColor = (diffValue, chartModel, isKPIWithAnomalyV2Enabled = false, kpiDisplayColorArray = []) => {
    if (isNumberInfinite(diffValue)) {
        return infiniteComparisionColor;
    }
    const isPositive = isNumberPositive(diffValue);
    let fallbackColor;
    const hasCustomColors = isKPIChartEnhancementsEnabled() &&
        kpiDisplayColorArray &&
        kpiDisplayColorArray.length >= 2;
    if (hasCustomColors) {
        const upwardColor = kpiDisplayColorArray[0];
        const downwardColor = kpiDisplayColorArray[1];
        fallbackColor = isPositive ? upwardColor : downwardColor;
    }
    else if (isPositive) {
        fallbackColor = isKPIWithAnomalyV2Enabled
            ? getPositiveComparisionColorForNewKPI(chartModel)
            : getPositiveComparisionColor(chartModel);
    }
    else {
        fallbackColor = isKPIWithAnomalyV2Enabled
            ? getNegativeComparisionColorForNewKPI(chartModel)
            : getNegativeComparisionColor(chartModel);
    }
    const cssVarName = isPositive
        ? '--ts-var-kpi-positive-change-color'
        : '--ts-var-kpi-negative-change-color';
    const unresolvedChangeColor = `var(${cssVarName}, ${fallbackColor})`;
    const isVariableApplied = isCSSVariableApplied(unresolvedChangeColor);
    if (isVariableApplied) {
        return unresolvedChangeColor;
    }
    return fallbackColor;
};
const KPI_CHANGE_CONTENT_TOKENS = {
    increase: '--rd-sys-color-content-success',
    decrease: '--rd-sys-color-content-failure',
};
export const getDefaultChangeStyle = (diffValue, chartModel, isKPIWithAnomalyV2Enabled = false, kpiDisplayColorArray = []) => {
    if (isNumberInfinite(diffValue)) {
        return null;
    }
    const changeColor = getChangeColor(diffValue, chartModel, isKPIWithAnomalyV2Enabled, kpiDisplayColorArray);
    if (_.startsWith(changeColor, 'var(')) {
        return null;
    }
    const isPositive = isNumberPositive(diffValue);
    const colorIndex = isPositive
        ? positiveComparisionColorIndex
        : negativeComparisionColorIndex;
    const isDefaultColor = _.some([
        chartSpecificConstants.DEFAULT_KPI_COLORS[colorIndex],
        chartSpecificConstants.DEFAULT_NEW_KPI_COLORS[colorIndex],
    ], (defaultColor) => _.toLower(defaultColor) === _.toLower(changeColor));
    if (!isDefaultColor) {
        return null;
    }
    const direction = isPositive ? 'increase' : 'decrease';
    return {
        direction,
        iconColor: `var(${KPI_CHANGE_CONTENT_TOKENS[direction]}, ${changeColor})`,
    };
};
export const getAbsoluteDiffValueToBeDisplayed = (absoluteDiffValue) => {
    return formatDataValue(absoluteDiffValue / 100, {
        nDecimal: absoluteDiffValue === 0 ? 0 : 2,
        isPercent: true,
    });
};
export const isSparklineEnabled = (chartModel) => {
    var _a, _b;
    return (_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showSparkline) !== null && _b !== void 0 ? _b : isKpiChartTypeTimeseries(chartModel);
};
export const isCurrentChartSparkline = (chartModel) => {
    const yAxisColumn = _.first(getYAxisColumns(chartModel));
    const isYAxisColumnMeasure = isMeasure(yAxisColumn);
    return isSparklineEnabled(chartModel) && isYAxisColumnMeasure;
};
const COMPARISON_GAP_OPTIONS = {
    [ColumnTimeBucket.Daily]: [
        TupleType.PrevAvailable,
        TupleType.PrevDay,
        TupleType.PrevWeek,
        TupleType.PrevMonth,
        TupleType.PrevQuarter,
        TupleType.PrevYear,
    ],
    [ColumnTimeBucket.Weekly]: [
        TupleType.PrevAvailable,
        TupleType.PrevWeek,
        TupleType.PrevMonth,
        TupleType.PrevQuarter,
        TupleType.PrevYear,
    ],
    [ColumnTimeBucket.Monthly]: [
        TupleType.PrevAvailable,
        TupleType.PrevMonth,
        TupleType.PrevQuarter,
        TupleType.PrevYear,
    ],
    [ColumnTimeBucket.Quarterly]: [
        TupleType.PrevAvailable,
        TupleType.PrevQuarter,
        TupleType.PrevYear,
    ],
    [ColumnTimeBucket.Yearly]: [TupleType.PrevAvailable, TupleType.PrevYear],
    [ColumnTimeBucket.Hourly]: [TupleType.PrevAvailable, TupleType.PrevHour],
};
export const TIME_BUCKET_FOR_GAP_OPTION = {
    [TupleType.PrevDay]: ColumnTimeBucket.Daily,
    [TupleType.PrevHour]: ColumnTimeBucket.Hourly,
    [TupleType.PrevMonth]: ColumnTimeBucket.Monthly,
    [TupleType.PrevWeek]: ColumnTimeBucket.Weekly,
    [TupleType.PrevQuarter]: ColumnTimeBucket.Quarterly,
    [TupleType.PrevYear]: ColumnTimeBucket.Yearly,
    [TupleType.PrevAvailable]: 'NO_BUCKET',
    [TupleType.Custom]: 'NO_BUCKET',
};
const LEGACY_COMPARISON_ENUM_MAPPER = {
    1: TupleType.Current,
    2: TupleType.PrevAvailable,
    3: TupleType.PrevHour,
    4: TupleType.PrevDay,
    5: TupleType.PrevWeek,
    6: TupleType.PrevMonth,
    7: TupleType.PrevQuarter,
    8: TupleType.PrevYear,
    9: TupleType.LastAvailable,
};
const ABBREVIATED_PREVIOUS_DATE_MAP = {
    [timeBuckets.MONTHLY]: 'PM',
    [timeBuckets.YEARLY]: 'PY',
    [timeBuckets.DAILY]: 'PD',
    [timeBuckets.WEEKLY]: 'PW',
    [timeBuckets.HOURLY]: 'PH',
    [timeBuckets.QUARTERLY]: 'PQ',
};
export const getBucketLabels = _.once(() => ({
    [ColumnTimeBucket.Daily]: translateLazy('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_DAY'),
    [ColumnTimeBucket.Weekly]: translateLazy('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_WEEK'),
    [ColumnTimeBucket.Monthly]: translateLazy('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_MONTH'),
    [ColumnTimeBucket.Quarterly]: translateLazy('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_QUARTER'),
    [ColumnTimeBucket.Yearly]: translateLazy('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_YEAR'),
    [ColumnTimeBucket.Hourly]: translateLazy('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_HOUR'),
    NO_BUCKET: translateLazy('charts.chartEditor.KPI.COMPARE_WITH_PREV_VALUE'),
}));
export const shouldWrapCustomLabel = (label) => {
    const words = label.split(/\s+/);
    const hasLongWord = words.some(word => word.length > 8);
    return hasLongWord;
};
export const getPreviousDateOptions = (columnTimeBucket) => {
    return translate('charts.chartEditor.KPI.DEFAULT_COMPARE_WITH_OPTION_LABEL', { bucket: getBucketLabels()[columnTimeBucket]() });
};
export const getPreviousDateOptionsFromRefTupleType = (refTupleType) => {
    return translate('charts.chartEditor.KPI.DEFAULT_COMPARE_WITH_OPTION_LABEL', {
        bucket: getBucketLabels()[TIME_BUCKET_FOR_GAP_OPTION[refTupleType]](),
    });
};
export const getPreviousDateOptionsFromTimeBucket = (chartModel) => {
    const xAxisColumn = _.first(getXAxisColumns(chartModel));
    const currentCustomProps = getChartSpecificCustomProps(chartModel) || {};
    const genericCompareValue = currentCustomProps.genericCompareValue;
    const timeBucket = mapTimeBucketToCategory(getTimeBucket(xAxisColumn), genericCompareValue === '1');
    return translate('charts.chartEditor.KPI.DEFAULT_COMPARE_WITH_OPTION_LABEL', {
        bucket: timeBucket,
    }).toLowerCase();
};
export const getCustomCompareLabel = (refTupleType, columnTimeBucket) => {
    const labels = getBucketLabels();
    if (refTupleType === TupleType.PrevAvailable) {
        return translate('charts.chartEditor.KPI.PREVIOUS_COMPARE_WITH_OPTION_LABEL');
    }
    if (columnTimeBucket === TIME_BUCKET_FOR_GAP_OPTION[refTupleType]) {
        return getPreviousDateOptions(columnTimeBucket);
    }
    return translate('charts.chartEditor.KPI.CUSTOM_COMPARE_WITH_OPTION_LABEL', {
        gapOption: labels[TIME_BUCKET_FOR_GAP_OPTION[refTupleType]](),
        bucket: labels[columnTimeBucket](),
    });
};
export const getRefTupleDateTime = (chartModel, refTuple) => {
    var _a, _b, _c;
    const kpiData = _.get(chartModel, 'data.0.kpiData');
    const timeColumn = _.first(getXAxisColumns(chartModel));
    const columnDataLite = _.get(chartModel, 'data.0.columnDataLite');
    const timeColumnData = (_a = columnDataLite.find(colData => colData.columnId === (timeColumn || {}).id)) !== null && _a !== void 0 ? _a : {};
    if (_.isEmpty(kpiData)) {
        const epochValue = convertToMilliSeconds(_.get(timeColumnData, 'dataValue.0'));
        return getFormattedDateTime(epochValue, timeColumn);
    }
    const isCustomCalendarPresent = timeColumn && hasCustomCalendar(timeColumn);
    if (isCustomCalendarPresent) {
        const customCalendarOverridesFiscalOffset = getCustomCalendarGuidFromColumn(timeColumn) !==
            getCustomCalendarGuid(FISCAL_CALENDAR_NAME);
        const dataValue = timeColumnData === null || timeColumnData === void 0 ? void 0 : timeColumnData.dataValue;
        const dateTime = dataValue.find(data => { var _a; return Number((_a = data === null || data === void 0 ? void 0 : data.v) === null || _a === void 0 ? void 0 : _a.s) === Number(refTuple === null || refTuple === void 0 ? void 0 : refTuple.time); });
        if (dateTime === null || dateTime === void 0 ? void 0 : dateTime.d) {
            return dateTime === null || dateTime === void 0 ? void 0 : dateTime.d;
        }
        if ((_b = dateTime === null || dateTime === void 0 ? void 0 : dateTime.v) === null || _b === void 0 ? void 0 : _b.s) {
            const epochValue = convertToMilliSeconds(Number((_c = dateTime === null || dateTime === void 0 ? void 0 : dateTime.v) === null || _c === void 0 ? void 0 : _c.s));
            return getFormattedDateTime(epochValue, timeColumn, true, {
                customCalendarOverridesFiscalOffset,
            });
        }
    }
    const epochValue = convertToMilliSeconds(_.get(refTuple, 'time'));
    return getFormattedDateTime(epochValue, timeColumn);
};
export const getRefTuples = (chartModel) => {
    return _.map(_.get(chartModel, 'data.0.kpiData.refTuples'));
};
export const getRefTuple = (chartModel, refTupleType) => {
    return _.find(getRefTuples(chartModel), {
        type: refTupleType,
    });
};
export const getEffectiveRefTuple = (chartModel) => {
    var _a;
    const kpiChartEnhancementsEnabled = isKPIChartEnhancementsEnabled();
    const customProps = getKpiColumnCustomProps(chartModel);
    const customComparison = kpiChartEnhancementsEnabled
        ? customProps === null || customProps === void 0 ? void 0 : customProps.customCompare
        : (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.customCompare;
    let tupleType = customComparison !== null && customComparison !== void 0 ? customComparison : TupleType.PrevAvailable;
    if (!Number.isNaN(Number.parseInt(tupleType, 10))) {
        tupleType =
            LEGACY_COMPARISON_ENUM_MAPPER[Number.parseInt(tupleType, 10)];
    }
    let refTuple = getRefTuple(chartModel, tupleType);
    if (!kpiChartEnhancementsEnabled &&
        (refTuple || tupleType !== TupleType.PrevAvailable)) {
        return refTuple;
    }
    if (kpiChartEnhancementsEnabled && !refTuple) {
        if (tupleType) {
            const refTuples = getRefTuples(chartModel);
            refTuple = refTuples.find(tuple => String(tuple.type) === tupleType);
        }
        const prevAvailableTuple = getRefTuple(chartModel, TupleType.PrevAvailable);
        if (prevAvailableTuple) {
            return prevAvailableTuple;
        }
    }
    if (refTuple) {
        return refTuple;
    }
    const legacyRefTuple = _.get(chartModel, 'data.0.kpiData.refTuple');
    if (legacyRefTuple) {
        _.set(legacyRefTuple, 'relativePercentChange', _.get(chartModel, 'data.0.kpiData.diffValuePercent'));
        return legacyRefTuple;
    }
    return undefined;
};
export const getCurrentRefTuple = (chartModel) => {
    const currentTuple = getRefTuple(chartModel, TupleType.Current);
    if (currentTuple) {
        return currentTuple;
    }
    const legacyCurrentTuple = _.get(chartModel, 'data.0.kpiData.currentTuple');
    if (legacyCurrentTuple) {
        return legacyCurrentTuple;
    }
    return undefined;
};
export const getRefTupleDateTimeLabel = (chartModel, tuple, isAggregationEnabled, aggregationGranularity) => {
    if (!tuple) {
        return '';
    }
    if (isAggregationEnabled) {
        return getRefTupleDateTimeForAggregationVal(chartModel, tuple, aggregationGranularity);
    }
    return getRefTupleDateTime(chartModel, tuple);
};
export const getCustomComparisonSublabel = (chartModel, refTuple, isKPIChartEnhancementEnabled = false) => {
    if (!refTuple) {
        return translate('charts.chartEditor.KPI.CUSTOM_COMPARE_OUT_OF_QUERY');
    }
    if (!refTuple.time) {
        return translate('charts.chartEditor.KPI.CUSTOM_COMPARE_DATA_MISSING');
    }
    if (isKPIChartEnhancementEnabled) {
        const isAggregationEnabled = isAggregationEnabledWithValue(chartModel);
        const tupleSelectionConfig = {
            isAggregationEnabled,
            isSinglePointEnabled: false,
        };
        const currentRefTuple = getAppropriateCurrentTuple(chartModel, tupleSelectionConfig);
        const effectiveRefTuple = getAppropriateRefTuple(chartModel, tupleSelectionConfig);
        const aggregationGranularity = isAggregationEnabled
            ? _.get(effectiveRefTuple, 'aggregationGranularity', 'DAY')
            : 'DAY';
        const getLabel = (tuple) => getRefTupleDateTimeLabel(chartModel, tuple, isAggregationEnabled, aggregationGranularity);
        const currentLabel = getLabel(currentRefTuple);
        const comparisonLabel = getLabel(refTuple);
        if (currentLabel && comparisonLabel) {
            return `${currentLabel} vs ${comparisonLabel}`;
        }
        return currentLabel || comparisonLabel;
    }
    return getRefTupleDateTime(chartModel, refTuple);
};
export const getCustomComparisonOptions = (chartModel, isKPIChartEnhancementEnabled = false) => {
    const xAxisColumn = _.first(getXAxisColumns(chartModel));
    if (!xAxisColumn) {
        return [];
    }
    const effectiveTimeBucket = xAxisColumn.timeBucket;
    const timeBucketGapOptions = COMPARISON_GAP_OPTIONS[effectiveTimeBucket];
    const refTuples = getRefTuples(chartModel);
    const refTuplesByType = _.keyBy(refTuples, 'type');
    const options = [
        {
            title: '',
            options: _.map(timeBucketGapOptions, (gapOption) => ({
                id: gapOption,
                label: getCustomCompareLabel(gapOption, effectiveTimeBucket),
                subText: getCustomComparisonSublabel(chartModel, refTuplesByType[gapOption], isKPIChartEnhancementEnabled),
                tooltip: getCustomCompareLabel(gapOption, effectiveTimeBucket),
            })),
        },
    ];
    if (isGenericCustomCompareEnabled()) {
        const customLabel = translate('dateFilter.dateFilterType.custom');
        const isDisabled = !getRefTuple(chartModel, TupleType.Custom);
        options.push({
            title: '',
            options: [
                {
                    id: TupleType.Custom,
                    label: customLabel,
                    tooltip: customLabel,
                    disabled: isDisabled,
                },
            ],
        });
    }
    return options;
};
export const getKpiCurrentValue = (chartModel) => {
    const columnDataLite = _.get(chartModel, 'data.0.columnDataLite');
    const kpiData = _.get(chartModel, 'data.0.kpiData');
    const yAxisColumn = _.first(getYAxisColumns(chartModel));
    const yAxisColumnData = columnDataLite && Array.isArray(columnDataLite)
        ? columnDataLite.find(colData => colData.columnId === (yAxisColumn || {}).id)
        : {};
    const currentRefTuple = getCurrentRefTuple(chartModel);
    return _.isEmpty(kpiData) || _.isEmpty(currentRefTuple)
        ? _.get(yAxisColumnData, 'dataValue.0')
        : currentRefTuple === null || currentRefTuple === void 0 ? void 0 : currentRefTuple.value;
};
export const hasCustomComparison = (chartModel) => {
    var _a;
    let customCompareOverride;
    if (isKPIChartEnhancementsEnabled()) {
        const customProps = getKpiColumnCustomProps(chartModel);
        customCompareOverride = customProps === null || customProps === void 0 ? void 0 : customProps.customCompare;
    }
    else {
        customCompareOverride = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.customCompare;
    }
    if (!_.isUndefined(customCompareOverride)) {
        if (!Number.isNaN(Number.parseInt(customCompareOverride, 10))) {
            customCompareOverride =
                LEGACY_COMPARISON_ENUM_MAPPER[Number.parseInt(customCompareOverride, 10)];
        }
        return customCompareOverride !== TupleType.PrevAvailable;
    }
    return false;
};
export const getIncompleteThreshold = (chartModel) => {
    var _a;
    const timeColumn = _.first(getXAxisColumns(chartModel));
    if (!timeColumn) {
        return Number.MAX_SAFE_INTEGER;
    }
    return ((_a = incompleteZoneConfigTemplate1.getIncompleteThreshold(getTimeBucket(timeColumn))) !== null && _a !== void 0 ? _a : Number.MAX_SAFE_INTEGER);
};
function isInvalidTooltipConfig(dataPoint) {
    if (!dataPoint) {
        return false;
    }
    const tooltipDataContent = dataPoint.tooltipDataContent;
    const xRawValues = dataPoint.xRawValues;
    return xRawValues === null || xRawValues === void 0 ? void 0 : xRawValues.some(xRawValue => {
        return !!_.find(tooltipDataContent, { dataValue: xRawValue });
    });
}
export function getKPISeriesXAxisValidationError(dataModel, chartModel, chartType, envOptions) {
    var _a;
    const series = getSeries(dataModel) || [];
    const chartPointXGetter = chartPoint => {
        return chartPoint.x;
    };
    for (let i = 0; i < series.length; i++) {
        const serie = series[i];
        if (arrayHasDuplicates(serie.data, chartPointXGetter)) {
            if ((envOptions === null || envOptions === void 0 ? void 0 : envOptions.flags.isTooltipCustomizationEnabled) &&
                isInvalidTooltipConfig((_a = serie.data) === null || _a === void 0 ? void 0 : _a[0])) {
                return ErrorTypes.INVALID_DATA_TOOLTIP_CONFIG;
            }
            return ErrorTypes.UNIQUE_X_VALUES_PER_SERIES;
        }
    }
    return null;
}
export const getXAxisColumnDataWithoutTrailingNulls = (chartModel, xAxisColumns) => {
    var _a, _b;
    if (!xAxisColumns.length || !((_b = (_a = chartModel.data) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.columnDataLite)) {
        return undefined;
    }
    const xAxisColumnData = chartModel.data[0].columnDataLite.find(columnData => columnData.columnId === xAxisColumns[0].id);
    if (!(xAxisColumnData === null || xAxisColumnData === void 0 ? void 0 : xAxisColumnData.dataValue)) {
        return xAxisColumnData;
    }
    return {
        ...xAxisColumnData,
        dataValue: xAxisColumnData.dataValue.filter(value => value !== null),
    };
};
export const isInCompleteKPI = (chartModel) => {
    const xAxisColumns = getXAxisColumns(chartModel);
    if (xAxisColumns.length) {
        const timeBucket = getTimeBucket(xAxisColumns[0]);
        const start = getTimeBucketStart(Date.now(), timeBucket);
        const threshold = subtractFromField(start, timeBucket, 1);
        const xAxisColumnData = getXAxisColumnDataWithoutTrailingNulls(chartModel, xAxisColumns);
        if (isCustomCalendarTimeSeries(chartModel)) {
            for (let i = 0; i < xAxisColumnData.dataValue.length; i++) {
                let endDate = null;
                if (!_.isNil(getEndEpochMS(xAxisColumnData.dataValue[i]))) {
                    endDate = getEndEpochMS(xAxisColumnData.dataValue[i]);
                }
                else {
                    const startDate = getStartEpochMS(xAxisColumnData.dataValue[i]);
                    endDate = getNextBucketEpoch(startDate, timeBucket);
                }
                if (endDate > Date.now())
                    return true;
            }
        }
        else {
            return xAxisColumnData.dataValue.some((epoch) => {
                return convertToMilliSeconds(epoch) > threshold;
            });
        }
    }
    return false;
};
export const mapTimeBucketToCategory = (timeBucket, trimLastChar = false) => {
    switch (timeBucket) {
        case 'd':
            return trimLastChar
                ? translate('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_DAY')
                : translate('dateFilter.day');
        case 'w':
            return trimLastChar
                ? translate('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_WEEK')
                : translate('dateFilter.week');
        case 'M':
            return trimLastChar
                ? translate('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_MONTH')
                : translate('dateFilter.month');
        case 'y':
            return trimLastChar
                ? translate('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_YEAR')
                : translate('dateFilter.year');
        case 'Q':
            return trimLastChar
                ? translate('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_QUARTER')
                : translate('dateFilter.quarter');
        case 'h':
            return trimLastChar
                ? translate('charts.chartEditor.KPI.COMPARE_WITH_BUCKET_HOUR')
                : translate('dateFilter.hour');
        default:
            return undefined;
    }
};
export const getChartAggregatedRefTuple = (chartModel) => {
    const chartAggregatedTuple = getRefTuple(chartModel, TupleType.ChartAggregatedVal);
    if (chartAggregatedTuple) {
        return chartAggregatedTuple;
    }
    return undefined;
};
export const getPreviousChartAggregatedRefTuple = (chartModel) => {
    const prevChartAggregatedTuple = getRefTuple(chartModel, TupleType.PreviousChartAggregatedVal);
    if (prevChartAggregatedTuple) {
        return prevChartAggregatedTuple;
    }
    return undefined;
};
export const getSinglePointRefTuple = (chartModel) => {
    const singlePointRefTuple = getRefTuple(chartModel, TupleType.SinglePointKpiCurrent);
    if (singlePointRefTuple) {
        return singlePointRefTuple;
    }
    return undefined;
};
export const getSinglePointPreviousRefTuple = (chartModel) => {
    const singlePointPrevRefTuple = getRefTuple(chartModel, TupleType.SinglePointKpiPrevious);
    if (singlePointPrevRefTuple) {
        return singlePointPrevRefTuple;
    }
    return undefined;
};
export const isSinglePointKPIEnabledWithOnlyCurrentValue = (chartModel) => {
    const hasCurrentSinglePointValue = getRefTuple(chartModel, TupleType.SinglePointKpiCurrent);
    return !!hasCurrentSinglePointValue;
};
export const isSinglePointKPIEnabledWithValue = (chartModel) => {
    const hasCurrentSinglePointValue = getRefTuple(chartModel, TupleType.SinglePointKpiCurrent);
    const hasPreviousSinglePointValue = getRefTuple(chartModel, TupleType.SinglePointKpiPrevious);
    return !!(hasCurrentSinglePointValue && hasPreviousSinglePointValue);
};
export const getAppropriateRefTuple = (chartModel, config = {}) => {
    const { isAggregationEnabled = false, isSinglePointEnabled = false, } = config;
    if (!chartModel) {
        logger.warn('getAppropriateRefTuple: Chart model is required for current tuple selection');
        return undefined;
    }
    try {
        const tupleSelectors = {
            default: () => getEffectiveRefTuple(chartModel),
            aggregation: () => getPreviousChartAggregatedRefTuple(chartModel),
            singlePoint: () => getSinglePointPreviousRefTuple(chartModel),
        };
        if (isAggregationEnabled) {
            return tupleSelectors.aggregation();
        }
        if (isSinglePointEnabled) {
            return tupleSelectors.singlePoint();
        }
        return tupleSelectors.default();
    }
    catch (error) {
        logger.warn('Error selecting ref tuple:', error);
        return undefined;
    }
};
export const getAppropriateCurrentTuple = (chartModel, config = {}) => {
    const { isAggregationEnabled = false, isSinglePointEnabled = false, } = config;
    if (!chartModel) {
        logger.warn('getAppropriateCurrentTuple: Chart model is required for current tuple selection');
        return undefined;
    }
    try {
        const tupleSelectors = {
            default: () => getCurrentRefTuple(chartModel),
            aggregation: () => getChartAggregatedRefTuple(chartModel),
            singlePoint: () => getSinglePointRefTuple(chartModel),
        };
        if (isAggregationEnabled) {
            return tupleSelectors.aggregation();
        }
        if (isSinglePointEnabled) {
            return tupleSelectors.singlePoint();
        }
        return tupleSelectors.default();
    }
    catch (error) {
        logger.warn('Error selecting current tuple:', error);
        return undefined;
    }
};
export const isAggregationEnabledWithValue = (chartModel) => {
    if (!isChartAggregatedValOnKPIEnabled()) {
        return false;
    }
    const hasCurrentAggregatedValue = getRefTuple(chartModel, TupleType.ChartAggregatedVal);
    const hasPreviousAggregatedValue = getRefTuple(chartModel, TupleType.PreviousChartAggregatedVal);
    return !!(hasCurrentAggregatedValue && hasPreviousAggregatedValue);
};
export const getFormattedDateTimeForAggregationVal = (epochMillis, endEpochMillis, timeBucket, shouldTranslateText = true) => {
    const formattedEpoch = formatEpochMillisToFiscalYear(epochMillis, timeBucket, getLabelFormat(timeBucket));
    const formattedEndEpoch = formatEpochMillisToFiscalYear(endEpochMillis, timeBucket, getLabelFormat(timeBucket));
    if (shouldTranslateText) {
        if (timeBucket === timeBucketCategories.WEEK) {
            return getTranslatedTextCallback()('aggregateWeeklyRange', {
                startDate: formattedEpoch,
                endDate: formattedEndEpoch,
            });
        }
        return getTranslatedTextCallback()('aggregateDailyRange', {
            startDate: formattedEpoch,
            endDate: formattedEndEpoch,
        });
    }
    return `${formattedEpoch} to ${formattedEndEpoch}`;
};
export const getRefTupleDateTimeForAggregationVal = (chartModel, refTuple, timeBucket) => {
    var _a;
    const kpiData = _.get(chartModel, 'data.0.kpiData');
    const timeColumn = _.first(getXAxisColumns(chartModel));
    const columnDataLite = _.get(chartModel, 'data.0.columnDataLite');
    const timeColumnData = (_a = columnDataLite.find(colData => colData.columnId === (timeColumn || {}).id)) !== null && _a !== void 0 ? _a : {};
    if (_.isEmpty(kpiData)) {
        const epochValue = convertToMilliSeconds(_.get(timeColumnData, 'dataValue.0'));
        return getFormattedDateTime(epochValue, timeColumn);
    }
    const epochMillis = convertToMilliSeconds(_.get(refTuple, 'time'));
    const endEpochMillis = convertToMilliSeconds(_.get(refTuple, 'endTime'));
    return getFormattedDateTimeForAggregationVal(epochMillis, endEpochMillis, timeBucketCategories[timeBucket]);
};
export const getColumnDataValueByColumnId = (columnDataLite, columnId) => {
    const columnData = columnDataLite.find(colData => colData.columnId === columnId);
    return _.last(columnData === null || columnData === void 0 ? void 0 : columnData.dataValue);
};
export const ensureYAxisLabelsHidden = (chart) => {
    var _a;
    if ((chart === null || chart === void 0 ? void 0 : chart.yAxis) && ((_a = chart.yAxis) === null || _a === void 0 ? void 0 : _a.length) > 0) {
        chart.yAxis.forEach((axis) => {
            axis.update({
                labels: { enabled: false },
                title: { enabled: false },
            }, false);
        });
    }
};
export const kpiChartEvents = {
    onLoad(chart, chartModel, hideAnalyze = false) {
        var _a;
        if (isKPIChartEnhancementsEnabled()) {
            const yAxisColumns = getYAxisColumns(chartModel);
            const hasNonDefaultMissingValues = yAxisColumns.some(col => {
                if (!(col === null || col === void 0 ? void 0 : col.id))
                    return false;
                const handleMissingValues = getHandleMissingValues(chartModel, col.id);
                return (handleMissingValues &&
                    handleMissingValues !== HandleMissingValues.DontShow);
            });
            if (hasNonDefaultMissingValues) {
                chart.redraw();
            }
        }
        if (getEnvFlags().isKPIWithAnomalyV2Enabled) {
            chart.customCrosshair = chart.renderer
                .path(['M', 0, 0, 'L', 0, 0])
                .attr({
                'stroke-width': 2,
                stroke: 'gray',
                zIndex: 5,
            })
                .add()
                .hide();
            const isCFApplied = isConditionalFormattingApplied(chartModel.vizProp, getAxisConfig(chartModel).y, 'ChartVizProps', chartModel);
            if (isCFApplied && !hideAnalyze) {
                addAnomalyExpectationBarAndLine(chart, chartModel, {
                    series: (_a = chart === null || chart === void 0 ? void 0 : chart.options) === null || _a === void 0 ? void 0 : _a.series,
                });
            }
            if (chartModel &&
                (isInCompleteKPI(chartModel) ||
                    !isShowAnomaliesEnabled(chartModel) ||
                    !hasCortexData(chartModel))) {
                chart.series.forEach((s) => {
                    const data = s.data;
                    if (data && data.length > 0) {
                        const lastPoint = data[data.length - 1];
                        if (!lastPoint.marker || !lastPoint.marker.enabled) {
                            if (lastPoint && lastPoint.update) {
                                lastPoint.update({
                                    marker: {
                                        enabled: true,
                                        lineWidth: 0,
                                        color: s.color,
                                    },
                                });
                            }
                        }
                    }
                });
            }
        }
    },
    onBeforeRender(chart, chartModel) {
        var _a;
        ensureYAxisLabelsHidden(chart);
        if (getEnvFlags().isKPIWithAnomalyV2Enabled &&
            chartModel &&
            (isInCompleteKPI(chartModel) ||
                !isShowAnomaliesEnabled(chartModel) ||
                !hasCortexData(chartModel))) {
            if ((_a = chart === null || chart === void 0 ? void 0 : chart.options) === null || _a === void 0 ? void 0 : _a.chart) {
                chart.options.chart.marginBottom = 0;
                chart.options.chart.marginRight = 8;
            }
        }
    },
    onUnselectPoint(point, chartModel) {
        if (!getEnvFlags().isKPIWithAnomalyV2Enabled) {
            const isAnomaly = isPointAnomaly(chartModel, point.options.xRawValues[0]);
            if (isAnomaly) {
                point.update({
                    marker: {
                        enabled: true,
                    },
                    color: getAnomalyColor(chartModel),
                });
            }
            else {
                point.update({ marker: {} });
            }
            return;
        }
        if (getEnvFlags().isKPIWithAnomalyV2Enabled) {
            const pointsLength = point.series.points.length;
            const lastPoint = point.series.points[pointsLength - 1];
            const isLastPoint = point.options.xRawValues[0] === lastPoint.xRawValues[0];
            let markerContent = {};
            let colorForAnomalyV2 = {};
            if (isLastPoint) {
                markerContent = {
                    enabled: true,
                    lineWidth: 0,
                };
                colorForAnomalyV2 = {
                    color: point.color || point.series.color,
                };
            }
            point.update({
                marker: {
                    ...markerContent,
                },
                ...colorForAnomalyV2,
            });
        }
    },
};
export const getAdditionalInlineStylesForComparison = ({ absoluteDiffValue, bgColor, }) => {
    const styles = {};
    if (absoluteDiffValue === 0) {
        styles.backgroundColor = zeroDiffValueBgColor;
        styles.color = zeroDiffValueColor;
        return styles;
    }
    if (bgColor) {
        styles.backgroundColor = bgColor;
        return styles;
    }
    return styles;
};
export const getErrorTitleForCustomCompare = (alertMessageType) => {
    if (alertMessageType === ErrorCodes.COMPARISON_DATE_OUT_OF_RANGE ||
        (isGenericCustomCompareEnabled() &&
            alertMessageType === ErrorCodes.MAX_OFFSET_LIMIT)) {
        return translate('charts.KPI.COMPARISON_OUT_OF_BOUND_ALERT.TITLE');
    }
    return null;
};
export const getErrorDescriptionForCustomCompare = ({ alertMessageType, refTupleType, chartModel, }) => {
    if (alertMessageType === ErrorCodes.COMPARISON_DATE_OUT_OF_RANGE) {
        return translate('charts.KPI.COMPARISON_OUT_OF_BOUND_ALERT.DESCRIPTION', {
            referencedDateLabel: _.toLower(getPreviousDateOptionsFromRefTupleType(refTupleType)),
        });
    }
    if (alertMessageType === ErrorCodes.MAX_OFFSET_LIMIT &&
        isGenericCustomCompareEnabled()) {
        return translate('charts.KPI.COMPARISON_OUT_OF_BOUND_ALERT.DESCRIPTION', {
            referencedDateLabel: getPreviousDateOptionsFromTimeBucket(chartModel),
        });
    }
    return null;
};
export const getFullKPIValueOnHover = (column, rawKPIValue, fallBackKpiValue) => {
    if (!column || rawKPIValue == null) {
        return fallBackKpiValue;
    }
    if (!isMeasure(column)) {
        return fallBackKpiValue;
    }
    const baseFormat = getFormattingFromColumn(column);
    if (!baseFormat) {
        return fallBackKpiValue;
    }
    if (baseFormat.category === CategoryType.Percentage) {
        return getValueFormattedByConfig(rawKPIValue, baseFormat, {
            formatPattern: null,
            columnLocale: null,
        });
    }
    const format = {
        ...baseFormat,
        numberFormatConfig: baseFormat.numberFormatConfig
            ? { ...baseFormat.numberFormatConfig, unit: Unit.None }
            : null,
        currencyFormatConfig: baseFormat.currencyFormatConfig
            ? { ...baseFormat.currencyFormatConfig, unit: Unit.None }
            : null,
    };
    return getValueFormattedByConfig(rawKPIValue, format, {
        formatPattern: null,
        columnLocale: null,
    });
};
const getKpiColumnPropertiesFromColumn = (chartModel, columnId) => {
    var _a;
    if (!columnId) {
        return {};
    }
    const columnProperties = getColumnPropertiesById(chartModel.vizProp, columnId);
    return (_a = columnProperties === null || columnProperties === void 0 ? void 0 : columnProperties.kpiColumnProperties) !== null && _a !== void 0 ? _a : {};
};
export const getKpiColumnProperties = (chartModel) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const xAxisColumnId = (_a = _.first(getXAxisColumns(chartModel))) === null || _a === void 0 ? void 0 : _a.id;
    const yAxisColumnId = (_b = _.first(getYAxisColumns(chartModel))) === null || _b === void 0 ? void 0 : _b.id;
    const xAxisKpiProps = getKpiColumnPropertiesFromColumn(chartModel, xAxisColumnId);
    const yAxisKpiProps = getKpiColumnPropertiesFromColumn(chartModel, yAxisColumnId);
    return {
        showAbbreviatedPreviousDate: (_c = xAxisKpiProps === null || xAxisKpiProps === void 0 ? void 0 : xAxisKpiProps.showAbbreviatedPreviousDate) !== null && _c !== void 0 ? _c : false,
        abbreviatedPreviousDate: (_d = xAxisKpiProps === null || xAxisKpiProps === void 0 ? void 0 : xAxisKpiProps.abbreviatedPreviousDate) !== null && _d !== void 0 ? _d : undefined,
        showComparisonDate: (_e = xAxisKpiProps === null || xAxisKpiProps === void 0 ? void 0 : xAxisKpiProps.showComparisonDate) !== null && _e !== void 0 ? _e : true,
        showCurrentDateLabel: (_f = xAxisKpiProps === null || xAxisKpiProps === void 0 ? void 0 : xAxisKpiProps.showCurrentDateLabel) !== null && _f !== void 0 ? _f : true,
        showPreviousDateLabel: (_g = xAxisKpiProps === null || xAxisKpiProps === void 0 ? void 0 : xAxisKpiProps.showPreviousDateLabel) !== null && _g !== void 0 ? _g : true,
        showPreviousValue: (_h = xAxisKpiProps === null || xAxisKpiProps === void 0 ? void 0 : xAxisKpiProps.showPreviousValue) !== null && _h !== void 0 ? _h : true,
        showSparkline: (_j = yAxisKpiProps === null || yAxisKpiProps === void 0 ? void 0 : yAxisKpiProps.showSparkline) !== null && _j !== void 0 ? _j : true,
        handleNullData: (_k = yAxisKpiProps === null || yAxisKpiProps === void 0 ? void 0 : yAxisKpiProps.handleNullData) !== null && _k !== void 0 ? _k : null,
    };
};
export const getKpiDisplayProperties = (chartModel) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const kpiDisplayProperties = _.get((_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.vizProp) === null || _a === void 0 ? void 0 : _a.chartProperties, 'kpiDisplayProperties') || {};
    const legacyShowAnalyseChangeCta = (_b = getChartSpecificCustomProps(chartModel)) === null || _b === void 0 ? void 0 : _b.showAnalyseChangeCta;
    const showAnalyseChangeCta = (kpiDisplayProperties === null || kpiDisplayProperties === void 0 ? void 0 : kpiDisplayProperties.showAnalyseChangeCta) === false &&
        legacyShowAnalyseChangeCta === true
        ? true
        : (_d = (_c = kpiDisplayProperties === null || kpiDisplayProperties === void 0 ? void 0 : kpiDisplayProperties.showAnalyseChangeCta) !== null && _c !== void 0 ? _c : legacyShowAnalyseChangeCta) !== null && _d !== void 0 ? _d : true;
    return {
        showChange: (_e = kpiDisplayProperties === null || kpiDisplayProperties === void 0 ? void 0 : kpiDisplayProperties.showChange) !== null && _e !== void 0 ? _e : true,
        showChangeAs: (_f = kpiDisplayProperties === null || kpiDisplayProperties === void 0 ? void 0 : kpiDisplayProperties.showChangeAs) !== null && _f !== void 0 ? _f : ShowChangeAs.Percent,
        changeInterpretation: (_g = kpiDisplayProperties === null || kpiDisplayProperties === void 0 ? void 0 : kpiDisplayProperties.changeInterpretation) !== null && _g !== void 0 ? _g : ChangeInterpretation.UpwardIsGood,
        kpiDisplayColorArray: (_h = kpiDisplayProperties === null || kpiDisplayProperties === void 0 ? void 0 : kpiDisplayProperties.kpiDisplayColorArray) !== null && _h !== void 0 ? _h : [],
        linkChangeColorsWithAnomaly: (_j = kpiDisplayProperties === null || kpiDisplayProperties === void 0 ? void 0 : kpiDisplayProperties.linkChangeColorsWithAnomaly) !== null && _j !== void 0 ? _j : true,
        showAnalyseChangeCta,
    };
};
export const getDefaultAbbreviatedPreviousDateFromChartModel = (chartModel) => {
    const xAxisColumn = _.first(getXAxisColumns(chartModel));
    if (!xAxisColumn) {
        return '';
    }
    const timeBucket = getTimeBucket(xAxisColumn);
    return ABBREVIATED_PREVIOUS_DATE_MAP[timeBucket] || '';
};
export const getAbbreviatedPreviousDate = (chartModel, showAbbreviatedPreviousDate, kpiColumnProps) => {
    if (!showAbbreviatedPreviousDate) {
        return getDefaultAbbreviatedPreviousDateFromChartModel(chartModel);
    }
    if ((kpiColumnProps === null || kpiColumnProps === void 0 ? void 0 : kpiColumnProps.abbreviatedPreviousDate) === '') {
        return undefined;
    }
    if (kpiColumnProps === null || kpiColumnProps === void 0 ? void 0 : kpiColumnProps.abbreviatedPreviousDate) {
        return kpiColumnProps.abbreviatedPreviousDate;
    }
    return getDefaultAbbreviatedPreviousDateFromChartModel(chartModel);
};
export const parseCustomProps = (customPropsValue) => {
    if (!customPropsValue) {
        return {};
    }
    if (typeof customPropsValue !== 'string') {
        return customPropsValue;
    }
    try {
        return JSON.parse(customPropsValue);
    }
    catch (_a) {
        return {};
    }
};
export const stringifyCustomProps = (customPropsValue, field, value) => {
    const parsed = parseCustomProps(customPropsValue);
    if (value === undefined || value === null || value === '') {
        delete parsed[field];
    }
    else {
        parsed[field] = value;
    }
    return JSON.stringify(parsed);
};
export const getGenericCompareValueFromCustomProps = (customPropsValue) => {
    var _a;
    const parsedValue = (_a = parseCustomProps(customPropsValue)) === null || _a === void 0 ? void 0 : _a.genericCompareValue;
    if (_.isUndefined(parsedValue) ||
        parsedValue === null ||
        parsedValue === '') {
        return undefined;
    }
    return String(parsedValue);
};
export const getKpiColumnCustomProps = (chartModel, columnId) => {
    var _a, _b, _c;
    if (!chartModel) {
        return {};
    }
    const resolvedColumnId = columnId ||
        (chartModel ? (_a = _.first(getXAxisColumns(chartModel))) === null || _a === void 0 ? void 0 : _a.id : undefined);
    const columnCustomProps = resolvedColumnId
        ? getColumnCustomProps(chartModel.vizProp, resolvedColumnId)
        : undefined;
    const chartSpecificCustomProps = getChartSpecificCustomProps(chartModel) || {};
    return {
        ...columnCustomProps,
        customCompare: (_b = columnCustomProps === null || columnCustomProps === void 0 ? void 0 : columnCustomProps.customCompare) !== null && _b !== void 0 ? _b : chartSpecificCustomProps.customCompare,
        genericCompareValue: (_c = columnCustomProps === null || columnCustomProps === void 0 ? void 0 : columnCustomProps.genericCompareValue) !== null && _c !== void 0 ? _c : chartSpecificCustomProps.genericCompareValue,
    };
};
export const updateKpiColumnCustomPropsField = (clientState, chartModel, columnId, field, value) => {
    var _a;
    const resolvedColumnId = columnId ||
        (chartModel ? (_a = _.first(getXAxisColumns(chartModel))) === null || _a === void 0 ? void 0 : _a.id : undefined);
    if (!resolvedColumnId ||
        !(clientState === null || clientState === void 0 ? void 0 : clientState.columnProperties) ||
        !clientState.columnProperties[resolvedColumnId]) {
        return clientState;
    }
    const columnProperty = clientState.columnProperties[resolvedColumnId];
    const updatedCustomProps = stringifyCustomProps(columnProperty.customProps, field, value);
    return {
        ...clientState,
        columnProperties: {
            ...clientState.columnProperties,
            [resolvedColumnId]: {
                ...columnProperty,
                customProps: updatedCustomProps,
            },
        },
    };
};
export function getAbsoluteDifferenceFormatted(effectiveRefTuple, currentRefTuple, yAxisColumn) {
    var _a, _b;
    const effectiveValue = (_a = effectiveRefTuple === null || effectiveRefTuple === void 0 ? void 0 : effectiveRefTuple.value) !== null && _a !== void 0 ? _a : 0;
    const currentValue = (_b = currentRefTuple === null || currentRefTuple === void 0 ? void 0 : currentRefTuple.value) !== null && _b !== void 0 ? _b : 0;
    const absoluteDifference = Math.abs(effectiveValue - currentValue);
    return formatDataValue(absoluteDifference, {
        noShorten: false,
        isDouble: true,
        nDecimal: absoluteDifference === 0 ? 0 : 2,
    });
}
//# sourceMappingURL=kpi-utils.js.map